<template>
	<div :class="getComponentClass">
		<template v-for="(tab, index) in tabs" :key="uid + '_' + index">
			<button
				v-if="Array.isArray(tab)"
				:id="uid + '_' + index"
				:class="getTabClass(tab)"
				@mouseenter="onArrayTabEnter(index)"
				@mouseleave="onArrayTabLeave"
			>
				{{ getArrayTabName(tab) }}

				<VIcon material="arrow_drop_down" />
			</button>

			<button
				v-else
				:class="getTabClass(tab)"
				@click="onTabClick(tab)"
			>
				{{ tab.name }}
			</button>
		</template>

		<VPopup
			ref="popupRef"
			class="tab-hierarchy-popup"
			:target="popupTarget"
			:offset="4"
			position="bottom"
			align="middle"
			:visible="popupVisible"
			@mouseleave="popupMouseLeave"
		>
			<div
				class="tab-hierarchy-popup-content"
				:class="{ 'have-active': popupHaveActiveTab(visibleTabChildren) }"
			>
				<button
					v-for="tabChild in visibleTabChildren"
					:key="tabChild.id"
					:class="getTabClass(tabChild)"
					@click="onTabChildClick(tabChild)"
				>
					{{ tabChild.name }}
				</button>
			</div>
		</VPopup>
	</div>
</template>

<script>
import VIcon            from '@/lib/components/VIcon/VIcon.vue';
import VPopup           from '@/lib/components/VPopup/VPopup.vue';
import  { TABS_THEMES } from '@/lib/components/VTabs/VTabs.vue';
import uniqueIdMixin    from "@/lib/mixins/component-unique-id";

export default defineComponent({
	name: 'VTabsHierarchy',

	components: {
		VIcon,
		VPopup,
	},

	mixins: [uniqueIdMixin],

	props: {
		tabs: {
			type: Array,
			default: null,
		},
		activeTab: {
			type: Object,
			default: null,
		},
		theme: {
			type: String,
			default: null,
			validator: v => TABS_THEMES.includes(v),
		},
	},

	emits: ['update:active-tab'],

	setup(props, { emit }) {
		const instance = getCurrentInstance();
		const popupRef = ref(null);
		const visibleTabChildren = ref([]);
		const popupTarget = ref(null);

		const activeTabValid = computed(() => {
			return (
				instance.proxy.$core?.utils?.isObject(props.activeTab)
				&& props.activeTab.hasOwnProperty('id')
				&& props.activeTab.hasOwnProperty('name')
			);
		});

		const popupVisible = computed(() => {
			return typeof popupTarget.value === 'string';
		});

		const getComponentClass = computed(() => {
			const result = {
				'tab-hierarchy-component': true,
			};
			if (props.theme) {
				result[props.theme] = true;
			}
			return result;
		});

		const getArrayTabName = (tab) => {
			if (!activeTabValid.value) return tab[0].name;
			const index = tab.findIndex(item => item.id === props.activeTab.id);
			return index > -1 ? tab[index].name : tab[0].name;
		};

		const getTabClass = (tab) => {
			const result = { 'tab-item': true };
			if (!activeTabValid.value) return result;

			if (Array.isArray(tab)) {
				result.active = tab.findIndex(item => item.id === props.activeTab.id) > -1;
			} else {
				result.active = tab.id === props.activeTab.id;
			}
			return result;
		};

		const onArrayTabEnter = (index) => {
			visibleTabChildren.value = props.tabs[index];
			nextTick().then(() => {
				popupTarget.value = `#${instance.proxy.uid}_${index}`;
			});
		};

		const hidePopup = () => {
			popupTarget.value = null;
			nextTick().then(() => {
				setTimeout(() => {
					visibleTabChildren.value = [];
				}, 150);
			});
		};

		const onArrayTabLeave = (event) => {
			if (!popupVisible.value) return;
			const x = event.clientX || event.pageX;
			const y = event.clientY || event.pageY;
			const popupTargetEl = instance.proxy.$el?.querySelector(popupTarget.value);
			const hoverElement = document.elementFromPoint(x, y);

			const popupEl = popupRef.value?.$el || popupRef.value;
			if (!popupTargetEl?.contains(hoverElement) && !popupEl?.contains(hoverElement)) {
				hidePopup();
			}
		};

		const popupMouseLeave = (event) => {
			if (!popupVisible.value) return;
			const x = event.clientX || event.pageX;
			const y = event.clientY || event.pageY;
			const popupTargetEl = instance.proxy.$el?.querySelector(popupTarget.value);
			const hoverElement = document.elementFromPoint(x, y);

			const popupEl = popupRef.value?.$el || popupRef.value;
			if (!popupTargetEl?.contains(hoverElement) && !popupEl?.contains(hoverElement)) {
				hidePopup();
			}
		};

		const onTabClick = (tab) => {
			emit('update:active-tab', tab);
		};

		const onTabChildClick = (tab) => {
			emit('update:active-tab', tab);
			hidePopup();
		};

		const popupHaveActiveTab = (children) => {
			return children.filter(item => item.id === props.activeTab.id).length > 0;
		};

		return {
			popupRef,
			visibleTabChildren,
			popupTarget,
			activeTabValid,
			popupVisible,
			getComponentClass,
			getArrayTabName,
			getTabClass,
			onArrayTabEnter,
			onArrayTabLeave,
			popupMouseLeave,
			hidePopup,
			onTabClick,
			onTabChildClick,
			popupHaveActiveTab,
		};
	},
});
</script>

<style lang="scss" src="./VTabsHierarchy.scss"/>
